import { Controller } from "@hotwired/stimulus"
import { showConfirmDialog, showAlert, reloadFolderSection } from "./confirm_dialog"

export default class extends Controller {
  static values = {
    targetId: String,
    confirmTitle: String,
    confirmLabel: String,
    cancelLabel: String,
    moveToRootConfirm: String,
    fileMoveToRootConfirm: String,
    moveFailed: String,
    folderFallbackName: String,
    fileFallbackName: String,
  }

  connect() {
    this._dragEnterCount = 0
    this._moveTimer = null
    this._moveActive = false
    this._deactivateTimeout = null

    // Show the drop zone when a drag starts on a subfolder
    document.addEventListener("dragstart", this._handleGlobalDragStart)
    document.addEventListener("dragend", this._handleGlobalDragEnd)
  }

  disconnect() {
    this._clearMoveTimer()
    if (this._deactivateTimeout) clearTimeout(this._deactivateTimeout)
    document.removeEventListener("dragstart", this._handleGlobalDragStart)
    document.removeEventListener("dragend", this._handleGlobalDragEnd)
  }

  _handleGlobalDragStart = () => {
    // Show drop zone when a folder or a file inside a folder is being dragged
    setTimeout(() => {
      const draggedFolder = document.querySelector("[data-dragging='true'][data-folder-id]")
      const draggedFile = document.querySelector("[data-dragging='true'][data-file-id]")
      if (draggedFolder) {
        this.element.classList.remove("hidden")
      } else if (
        draggedFile &&
        draggedFile.closest("[data-document-folders--folder-folder-id-value]")
      ) {
        // Only show drop zone for files that are inside a folder
        this.element.classList.remove("hidden")
      }
    }, 100)
  }

  _handleGlobalDragEnd = () => {
    this.element.classList.add("hidden")
    this._deactivateMove()
  }

  handleDragEnter(_event) {
    const draggedItem = this._getDraggedItem()
    if (!draggedItem) return

    // Cancel any pending deactivation
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }

    this._dragEnterCount++
    if (!this._moveTimer && !this._moveActive) {
      this._startMoveTimer()
    }
  }

  handleDragOver(event) {
    const draggedItem = this._getDraggedItem()
    if (!draggedItem) return

    // Always allow drop so the browser fires the drop event
    event.preventDefault()
    event.stopPropagation()

    // Cancel any pending deactivation — cursor is still over the drop zone
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }

    // Backup: start move timer if dragEnter was missed
    if (!this._moveTimer && !this._moveActive) {
      this._startMoveTimer()
    }
  }

  handleDragLeave(_event) {
    this._dragEnterCount--
    if (this._dragEnterCount <= 0) {
      this._dragEnterCount = 0
      // Debounce deactivation — SortableJS causes rapid leave+enter cycles
      if (this._deactivateTimeout) clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = setTimeout(() => {
        if (this._dragEnterCount <= 0) {
          this._deactivateMove()
        }
      }, 100)
    }
  }

  async handleDrop(event) {
    if (!this._moveActive) return

    event.preventDefault()
    event.stopPropagation()

    const draggedItem = this._getDraggedItem()
    if (!draggedItem) {
      this._deactivateMove()
      return
    }

    const isFile = !!draggedItem.dataset.fileId
    const moveUrl = draggedItem.dataset.moveUrl
    const draggedName = isFile
      ? draggedItem.dataset.fileName || this.fileFallbackNameValue
      : draggedItem.dataset.folderName || this.folderFallbackNameValue
    const messageTemplate = isFile ? this.fileMoveToRootConfirmValue : this.moveToRootConfirmValue
    const message = isFile
      ? messageTemplate.replace("%{file}", draggedName)
      : messageTemplate.replace("%{folder}", draggedName)

    draggedItem.dataset.movedIntoFolder = "true"
    this._deactivateMove()

    const confirmed = await showConfirmDialog({
      title: this.confirmTitleValue,
      message: message,
      confirmLabel: this.confirmLabelValue,
      cancelLabel: this.cancelLabelValue,
    })

    if (!confirmed) {
      delete draggedItem.dataset.movedIntoFolder
      return
    }

    this._performMove(moveUrl, isFile)
  }

  _getDraggedItem() {
    return (
      document.querySelector("[data-dragging='true'][data-folder-id]") ||
      document.querySelector("[data-dragging='true'][data-file-id]")
    )
  }

  _startMoveTimer() {
    this._clearMoveTimer()
    this._moveTimer = setTimeout(() => {
      this._activateMove()
    }, 500)
  }

  _clearMoveTimer() {
    if (this._moveTimer) {
      clearTimeout(this._moveTimer)
      this._moveTimer = null
    }
  }

  _activateMove() {
    this._moveActive = true
    this.element.classList.add("border-blue-400", "bg-blue-50", "text-blue-700")
    this.element.classList.remove("border-gray-300", "text-gray-500")
  }

  _deactivateMove() {
    this._clearMoveTimer()
    if (this._deactivateTimeout) {
      clearTimeout(this._deactivateTimeout)
      this._deactivateTimeout = null
    }
    this._moveActive = false
    this._dragEnterCount = 0
    this.element.classList.remove("border-blue-400", "bg-blue-50", "text-blue-700")
    this.element.classList.add("border-gray-300", "text-gray-500")
  }

  _performMove(moveUrl, isFile = false) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const body = isFile ? { new_folder_id: null } : { new_parent_id: null }

    fetch(moveUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token,
        Accept: "application/json",
      },
      body: JSON.stringify(body),
    })
      .then((response) => {
        if (response.ok) {
          reloadFolderSection(moveUrl)
        } else {
          response
            .json()
            .then((data) => {
              showAlert(data.error || this.moveFailedValue)
            })
            .catch(() => {
              showAlert(this.moveFailedValue)
            })
        }
      })
      .catch((error) => {
        console.error("Error moving item to root:", error)
        showAlert(this.moveFailedValue)
      })
  }
}
